import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as ObjectActions from './work-order-performance.actions';
import { of } from 'rxjs';
import * as AppActions from '../../app/actions';
import { Store } from '@ngrx/store';
import * as oeeAppReducer from '../../oee.reducer';
import { WorkOrderPerformanceService } from './work-order-performance.service';
import { GetWorkOrderResponseInterface } from '../../work-order-schedule/work-order-schedule.model';
import {
  BaseOneResponseInterface,
  GetManyResponseInterface,
} from '../../../shared/model/interface/crud-response-interface.model';
import { ActivityLogsResponseInterface, IActivityLog } from '../root-cause-analysis/root-cause-analysis.model';
import {
  IExpectedSpeed,
  IOeeCalculationResult,
  IWorkOrderComment,
  IWorkOrderLaborAssetInformation,
  IWorkOrderPhaseInformation, IWorkOrderResponseInterface,
  IWorkOrderStationActivityHistory,
} from './work-order-performance.model';
import { HelperService } from 'src/app/shared/service/helper.service';
import { EntityTranslatorService } from '../../../shared/service/entity-translator/entity-translator.service';
import { emptyAction } from '../../../../constants';
import { IPeriodicOeeCalculationData } from '../periodic-oee-calculation-review/periodic-oee-calculation-review.model';
import { CodeAndNameDisplayService } from '../../../shared/service/code-and-name-display/code-and-name-display.service';

@Injectable()
export class WorkOrderPerformanceEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    public service: WorkOrderPerformanceService,
    private readonly entityTranslatorService: EntityTranslatorService,
    private readonly codeAndNameDisplayService: CodeAndNameDisplayService,
  ) {}

  getWorkOrderPerformanceData = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.WORK_ORDER_INFORMATION_LOADING),
      switchMap((objectData: ObjectActions.WorkOrderInformationLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getWorkOrderInformation(objectData.params).pipe(
          switchMap((response: GetWorkOrderResponseInterface) => {
            this.entityTranslatorService.translate(response.data);
            return of(new ObjectActions.WorkOrderInformationLoaded(response.data), new AppActions.HideLoader());
          }),
          catchError((err) => {
            return of(new ObjectActions.FetchDataError(err));
          }),
        );
      }),
      catchError((err) => {
        return of(new ObjectActions.FetchDataError(err));
      }),
    ),
  );

  getWorkOrderActivityLogs = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.WORK_ORDER_ACTIVITY_LOGS_LOADING),
      switchMap((objectData: ObjectActions.WorkOrderActivityLogsLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getWorkOrderActivityLogs(objectData.params).pipe(
          switchMap((response: BaseOneResponseInterface<ActivityLogsResponseInterface>) => {
            response.data.rows.forEach((activityLog: IActivityLog) => {
              this.entityTranslatorService.translate(activityLog);
              activityLog.activityName = this.codeAndNameDisplayService.getDisplay({
                name: activityLog.activityName,
                code: activityLog.activityCode,
              });
              activityLog.taskName = this.codeAndNameDisplayService.getDisplay({
                name: activityLog.taskName,
                code: activityLog.taskCode,
              });
              this.service.setWorkOrderPerformanceTaskDescription(activityLog);
            });

            return of(new ObjectActions.WorkOrderActivityLogsLoaded(response.data), new AppActions.HideLoader());
          }),
          catchError((err) => {
            return of(new ObjectActions.FetchDataError(err));
          }),
        );
      }),
      catchError((err) => {
        return of(new ObjectActions.FetchDataError(err));
      }),
    ),
  );

  getWorkOrderOeeCalculationResult = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.WORK_ORDER_OEE_RESULT_LOADING),
      switchMap((objectData: ObjectActions.WorkOrderOeeResultLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOeeCalculationResult(objectData.params).pipe(
          switchMap((response: GetManyResponseInterface<IOeeCalculationResult>) => {
            return of(new ObjectActions.WorkOrderOeeResultLoaded(response.data[0]), new AppActions.HideLoader());
          }),
          catchError((err) => {
            return of(new ObjectActions.FetchDataError(err));
          }),
        );
      }),
      catchError((err) => {
        return of(new ObjectActions.FetchDataError(err));
      }),
    ),
  );

  getWorkOrderPhaseInformation = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.WORK_ORDER_PHASE_INFORMATION_LOADING),
      switchMap((objectData: ObjectActions.WorkOrderPhaseInformationLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getPhaseInformation(objectData.params).pipe(
          switchMap((response: BaseOneResponseInterface<IWorkOrderPhaseInformation>) => {
            return of(new ObjectActions.WorkOrderPhaseInformationLoaded(response.data), new AppActions.HideLoader());
          }),
          catchError((err) => {
            return of(new ObjectActions.FetchDataError(err));
          }),
        );
      }),
      catchError((err) => {
        return of(new ObjectActions.FetchDataError(err));
      }),
    ),
  );

  getWorkOrderLaborInformation = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.WORK_ORDER_LABOR_INFORMATION_LOADING),
      switchMap((objectData: ObjectActions.WorkOrderLaborInformationLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getLaborInformation(objectData.params).pipe(
          switchMap((response: BaseOneResponseInterface<IWorkOrderResponseInterface>) => {
            response.data.rows.forEach((workOrderLaborAssetInformation: IWorkOrderLaborAssetInformation) =>
              this.entityTranslatorService.translate(workOrderLaborAssetInformation),
            );

            return of(new ObjectActions.WorkOrderLaborInformationLoaded(response.data), new AppActions.HideLoader());
          }),
          catchError((err) => {
            return of(new ObjectActions.FetchDataError(err));
          }),
        );
      }),
      catchError((err) => {
        return of(new ObjectActions.FetchDataError(err));
      }),
    ),
  );

  getWorkOrderPeriodicOeeCalculations = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.WORK_ORDER_PERIODIC_OEE_CALCULATIONS_LOADING),
      switchMap((objectData: ObjectActions.WorkOrderPeriodicOeeCalculationsLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getWorkOrderPeriodicOeeCalculations(objectData.params).pipe(
          switchMap((response: GetManyResponseInterface<IPeriodicOeeCalculationData>) => {
            return of(new ObjectActions.WorkOrderPeriodicOeeCalculationsLoaded(response), new AppActions.HideLoader());
          }),
          catchError((err) => {
            return of(new ObjectActions.FetchDataError(err));
          }),
        );
      }),
      catchError((err) => {
        return of(new ObjectActions.FetchDataError(err));
      }),
    ),
  );

  downloadWorkOrderPerformanceExcel = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.DOWNLOAD_EXCEL),
      switchMap((objectData: ObjectActions.DownloadExcel) => {
        this.service.downloadExcel(objectData.data, objectData.excelType);
        return emptyAction;
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchDataError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getWorkOrderComments = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.COMMENTS_LOADING),
      switchMap((objectData: ObjectActions.CommentsLoading) => {
        return this.service.getWorkOrderComments(objectData.workOrderIds).pipe(
          switchMap((response: GetManyResponseInterface<IWorkOrderComment>) => {
            return of(new ObjectActions.CommentsLoaded(response.data));
          }),
          catchError((err) => {
            return of(new ObjectActions.FetchDataError(err));
          }),
        );
      }),
      catchError((err) => {
        return of(new ObjectActions.FetchDataError(err));
      }),
    ),
  );

  getWorkOrderStationActivityHistory = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.WORK_ORDER_STATION_ACTIVITY_HISTORY_LOADING),
      switchMap((objectData: ObjectActions.WorkOrderStationActivityHistoryLoading) => {

        return this.service.getManyStationActivityHistories(objectData.params).pipe(
          switchMap((response: BaseOneResponseInterface<IWorkOrderStationActivityHistory[]>) => {
            response.data.forEach((rowData: IWorkOrderStationActivityHistory) => {
              this.entityTranslatorService.translate(rowData);
              rowData.activityName = this.codeAndNameDisplayService.getDisplay({
                name: rowData.activityName,
                code: rowData.activityCode,
              });
              rowData.taskName = this.codeAndNameDisplayService.getDisplay({
                name: rowData.taskName,
                code: rowData.taskCode,
              });
              this.service.setWorkOrderPerformanceTaskDescription(rowData);
            });
            return of(new ObjectActions.WorkOrderStationActivityHistoryLoaded(response));
          }),
          catchError((err) => {
            return of(new ObjectActions.FetchDataError(err));
          }),
        );
      }),
      catchError((err) => {
        return of(new ObjectActions.FetchDataError(err));
      }),
    ),
  );
}
